import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { debounce } from "lodash";
import { queryCommonFlags } from "@/api/constants";

interface UseDropdownOptionsProps<T> {
  fetchFn?: (searchTerm?: string) => Promise<T>;
  transformData?: (data: T) => { label: string; value: any }[];
  staticOptions?: { label: string; value: any }[];
  searchable?: boolean;
  queryKey: string;
  staleTime?: number;
}

export const useDropdownOptions = <T>({
  fetchFn,
  transformData,
  staticOptions,
  searchable = false,
  queryKey,
  staleTime = 60000,
}: UseDropdownOptionsProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 300),
    []
  );

  const shouldFetch = !!fetchFn;

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [queryKey, searchable ? searchTerm : undefined],
    queryFn: shouldFetch ? () => fetchFn(searchTerm) : undefined,
    ...queryCommonFlags,
    staleTime,
    enabled: shouldFetch,
  });

  const options = useMemo(() => {
    if (staticOptions) return staticOptions;
    if (data && transformData) return transformData(data);
    return [];
  }, [data, staticOptions, transformData]);

  return {
    options,
    error,
    isLoading: fetchFn ? isLoading : false,
    handleSearchChange: searchable ? handleSearchChange : undefined,
    refetch,
    searchTerm,
  };
};
